import type { Member } from "../../../domain/app-types.ts";
import type { Category, TaskSort, Todo } from "../../../domain/entities.ts";
import { openTableColumns } from "./columns-dialog.ts";
import { openTableCellEditor } from "./cell-editor.ts";
import { normalizeTablePreferences, isTableColumn, isCustomColumn, type TablePreferences, type TableSort, type TableColumn } from "./preferences.ts";
import { sameTableEdit, tableEditPatch, tableEditSnapshot, validateTableDates, applyTablePatch } from "./edit.ts";
import { editableTableColumns } from "./view.ts";

export interface TaskTableCapabilities {
  customFields?(): import("../../../domain/entities.ts").CustomFieldDefinition[];
  preferences(): TablePreferences;
  savePreferences(value: TablePreferences): Promise<void>;
  inCollection(): boolean;
  scope(): string;
  listingSort(): { sortBy: TaskSort; sortDirection: "asc" | "desc" };
  setPage(page: number): void;
  task(id: string): Todo | undefined;
  category(id: string | null): Category | undefined;
  members(task: Todo): Member[];
  canEdit(id: string): boolean;
  mutate(id: string, update: (task: Todo) => void): Promise<Todo | undefined>;
  render(): Promise<void>;
  sync(): void;
  toast(message: string, error?: boolean): void;
  undo(message: string, revert: () => Promise<void>): void;
}

export function createTaskTableController(capabilities: TaskTableCapabilities) {
  let busy = false;
  const focusCell = (id: string, field: TableColumn) => {
    const target = Array.from(document.querySelectorAll<HTMLElement>("[data-table-edit]")).find(element => element.dataset.tableTask === id && element.dataset.tableEdit === field);
    (target || document.querySelector<HTMLElement>("[data-table-columns]"))?.focus({ preventScroll: true });
  };
  async function displayChange(operation: () => Promise<void>) {
    if (busy) return;
    busy = true;
    try { await operation(); } catch (error) { capabilities.toast(error instanceof Error ? error.message : "Could not update table settings.", true); }
    finally { busy = false; }
  }
  function bind(root: ParentNode) {
    root.querySelectorAll<HTMLElement>("[data-table-select-title]").forEach(button => button.addEventListener("click", () => {
      const checkbox = button.closest("tr")?.querySelector<HTMLInputElement>("[data-select-task]");
      if (checkbox && !checkbox.disabled) checkbox.click();
    }));
    root.querySelector<HTMLElement>("[data-table-columns]")?.addEventListener("click", () => {
      const scope = capabilities.scope();
      openTableColumns(capabilities.preferences(), capabilities.inCollection(), async columns => {
        if (scope !== capabilities.scope()) throw new Error("The location changed. Reopen table settings.");
        await capabilities.savePreferences({ ...capabilities.preferences(), columns }); await capabilities.render();
      }, capabilities.toast, capabilities.customFields?.());
    });
    root.querySelectorAll<HTMLElement>("[data-table-sort], [data-table-reset-sort]").forEach(button => button.addEventListener("click", () => void displayChange(async () => {
      const key = button.dataset.tableSort as TableSort | undefined;
      if (key && key !== "title" && key !== "status" && !isTableColumn(key)) return;
      const previous = capabilities.preferences();
      const inherited = capabilities.listingSort();
      const currentKey = previous.sortBy || inherited.sortBy, direction = previous.sortBy ? previous.direction : inherited.sortDirection;
      await capabilities.savePreferences(normalizeTablePreferences({ ...previous, sortBy: key || null, direction: key && currentKey === key && direction === "asc" ? "desc" : "asc" }, capabilities.inCollection()));
      capabilities.setPage(1); await capabilities.render();
      document.querySelector<HTMLElement>(key ? `[data-table-sort="${key}"]` : "[data-table-columns]")?.focus({ preventScroll: true });
    })));
    root.querySelectorAll<HTMLElement>("[data-table-edit]").forEach(button => button.addEventListener("click", () => {
      const id = button.dataset.tableTask || "", field = button.dataset.tableEdit as TableColumn;
      const task = capabilities.task(id);
      if (!task || !capabilities.canEdit(id) || task.deleted || task.archivedAt || capabilities.category(task.categoryId)?.archivedAt || !editableTableColumns.has(field) && !isCustomColumn(field)) return;
      const snapshot = tableEditSnapshot(task, field), location = task.categoryId;
      openTableCellEditor(task, field, capabilities.members(task), capabilities.category(task.categoryId), async value => {
        const current = capabilities.task(id);
        if (!current || !capabilities.canEdit(id) || current.deleted || current.archivedAt || capabilities.category(current.categoryId)?.archivedAt) throw new Error("This task is no longer editable.");
        if (current.categoryId !== location || !sameTableEdit(current, snapshot, field)) throw new Error("This field changed while the editor was open. Cancel and reopen it to review the latest value.");
        const patch = tableEditPatch(current, field, value, capabilities.members(current), capabilities.category(current.categoryId), isCustomColumn(field) ? capabilities.customFields?.().find(item => item.id === field.slice(6)) : undefined);
        if (sameTableEdit({ ...current, ...patch }, snapshot, field)) return;
        const updated = await capabilities.mutate(id, draft => {
          if (draft.categoryId !== location || !sameTableEdit(draft, snapshot, field)) throw new Error("This field changed. Cancel and reopen the editor.");
          if (field === "start" || field === "due") validateTableDates({ ...draft, ...patch });
          applyTablePatch(draft, patch, field);
        });
        if (!updated) throw new Error("The task could not be saved.");
        const saved = tableEditSnapshot(updated, field);
        capabilities.undo("Task updated", async () => {
          const latest = capabilities.task(id);
          if (!latest || !capabilities.canEdit(id) || latest.deleted || latest.archivedAt || latest.categoryId !== location || !sameTableEdit(latest, saved, field)) throw new Error("Cannot undo because this task changed or access was removed.");
          await capabilities.mutate(id, draft => {
            if (!sameTableEdit(draft, saved, field)) throw new Error("This field changed since the edit.");
            if (field === "start" || field === "due") validateTableDates({ ...draft, ...snapshot });
            applyTablePatch(draft, snapshot, field);
          });
          capabilities.sync(); await capabilities.render(); focusCell(id, field);
        });
        capabilities.sync(); await capabilities.render();
      }, capabilities.toast, () => focusCell(id, field), capabilities.customFields?.());
    }));
  }
  return { bind };
}
